import React, { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { SupportedLocale, getAllLanguages, getUITexts } from '../data';

interface LanguageSelectorProps {
  currentLocale: SupportedLocale;
  onLocaleChange: (locale: SupportedLocale) => void;
  className?: string;
  isMobile?: boolean;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  currentLocale,
  onLocaleChange,
  className = '',
  isMobile = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const languages = getAllLanguages();
  const uiTexts = getUITexts(currentLocale);
  const currentLanguage = languages.find(lang => lang.code === currentLocale);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleLanguageSelect = (locale: SupportedLocale) => {
    onLocaleChange(locale);
    setIsOpen(false);
    
    // Haptic feedback for mobile
    if (navigator.vibrate && isMobile) {
      navigator.vibrate(10);
    }
  };

  if (isMobile) {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-800/80 backdrop-blur-sm border border-gray-600/50 hover:border-gray-500/70 transition-all duration-300 active:scale-95"
          aria-label={uiTexts.a11y.languageSelector}
          title={`${uiTexts.languageSelector.current}: ${currentLanguage?.nativeName}`}
        >
          <span className="text-lg" role="img" aria-label={currentLanguage?.name}>
            {currentLanguage?.flag}
          </span>
        </button>

        {isOpen && (
          <>
            {/* Mobile backdrop */}
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" />
            
            {/* Mobile dropdown */}
            <div className="fixed bottom-0 left-0 right-0 bg-gray-900 border-t-2 border-gray-700 rounded-t-2xl z-50 animate-[slideInUp_0.3s_ease-out] pb-safe-bottom">
              <div className="p-4">
                {/* Handle bar */}
                <div className="w-12 h-1 bg-gray-600 rounded-full mx-auto mb-4" />
                
                <h3 className="text-lg font-bold text-white mb-4 text-center">
                  {uiTexts.languageSelector.title}
                </h3>
                
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {languages.map((language) => (
                    <button
                      key={language.code}
                      onClick={() => handleLanguageSelect(language.code as SupportedLocale)}
                      className={`w-full flex items-center justify-between p-4 rounded-xl transition-all duration-300 ${
                        language.code === currentLocale
                          ? 'bg-gradient-to-r from-pink-500/20 to-purple-500/20 border-2 border-pink-500/40'
                          : 'bg-gray-800/50 hover:bg-gray-700/70 border-2 border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl" role="img" aria-label={language.name}>
                          {language.flag}
                        </span>
                        <div className="text-left">
                          <div className="text-white font-medium">
                            {language.nativeName}
                          </div>
                          <div className="text-gray-400 text-sm">
                            {language.name}
                          </div>
                        </div>
                      </div>
                      {language.code === currentLocale && (
                        <Check className="text-pink-400" size={20} />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Desktop version
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-800/80 backdrop-blur-sm border border-gray-600/50 hover:border-gray-500/70 transition-all duration-300 text-white hover:bg-gray-700/80"
        aria-label={uiTexts.a11y.languageSelector}
        title={`${uiTexts.languageSelector.current}: ${currentLanguage?.nativeName}`}
      >
        {/* Show Globe icon only for Russian, others get flag + code */}
        {currentLocale === 'ru' && <Globe size={16} className="text-gray-400" />}
        <span className="text-sm font-medium flex items-center gap-1">
          {/* Show flag only for non-Russian languages, Russian already has Globe icon */}
          {currentLocale !== 'ru' && (
            <span role="img" aria-label={currentLanguage?.name}>
              {currentLanguage?.flag}
            </span>
          )}
          {currentLanguage?.code.toUpperCase()}
        </span>
        <ChevronDown 
          size={14} 
          className={`text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 right-0 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-50 min-w-48 animate-[fadeInUp_0.2s_ease-out]">
          <div className="p-2">
            <div className="text-xs font-medium text-gray-400 px-3 py-2 border-b border-gray-700/50 mb-1">
              {uiTexts.languageSelector.available}
            </div>
            {languages.map((language) => (
              <button
                key={language.code}
                onClick={() => handleLanguageSelect(language.code as SupportedLocale)}
                className={`w-full flex items-center justify-between p-3 rounded-lg transition-all duration-300 ${
                  language.code === currentLocale
                    ? 'bg-gradient-to-r from-pink-500/20 to-purple-500/20 text-white'
                    : 'text-gray-300 hover:bg-gray-800/70 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg" role="img" aria-label={language.name}>
                    {language.flag}
                  </span>
                  <div className="text-left">
                    <div className="font-medium text-sm">
                      {language.nativeName}
                    </div>
                    <div className="text-xs opacity-70">
                      {language.name}
                    </div>
                  </div>
                </div>
                {language.code === currentLocale && (
                  <Check className="text-pink-400" size={16} />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector; 